/* Shared helpers for the public broker submission pages.
   - Single source of truth for US_STATES + INDUSTRIES + LAND_USE + CONDITION.
   - PublicShell wraps the page with the Acquigine brand + a centered card.
*/
import { Link } from "react-router-dom";
import { CheckCircle2, LogOut, RefreshCw } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import SeoHead from "@/components/SeoHead";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY",
  "LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH",
  "OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

export const BIZ_INDUSTRIES = [
  "Retail", "Restaurant", "Service", "Manufacturing", "Distribution", "Healthcare", "Other",
];

export const LAND_USE = [
  "Vacant Residential", "Agricultural", "Timberland", "Commercial", "Industrial", "Mixed Use",
];

export const CONDITION = ["Excellent", "Good", "Fair", "Needs Renovation"];

export const ROLES = [
  { value: "owner", label: "I am the property/business owner" },
  { value: "broker", label: "I am a broker or authorized representative" },
  { value: "other", label: "Other (legal representative, executor, etc.)" },
];

// BLOCK 19 ,  timeline captured on the submit forms after seller-funnel merge.
export const TIMELINES = [
  { value: "asap", label: "ASAP ,  actively selling" },
  { value: "3_months", label: "Within 3 months" },
  { value: "6_months", label: "Within 6 months" },
  { value: "exploring", label: "Just exploring" },
];

export function PublicShell({ title, subtitle, children, testid, seoRoute }) {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-[#05070A] text-slate-100" data-testid={testid}>
      {seoRoute && <SeoHead route={seoRoute} />}
      <header className="sticky top-0 z-50 border-b border-white/8 bg-[#05070A]">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center group" data-testid="public-brand-home">
            <img
              src="/images/acquigine-logo.png"
              alt="Acquigine"
              className="h-20 md:h-[104px] w-auto object-contain"
              draggable={false}
            />
          </Link>
          {user ? (
            <div className="flex items-center gap-3 text-[11px] text-slate-400" data-testid="submit-signed-in-as">
              <span className="truncate max-w-[180px]">Signed in as {user.email}</span>
              <button onClick={logout} className="flex items-center gap-1 text-slate-500 hover:text-slate-200 transition" data-testid="submit-logout-btn">
                <LogOut className="h-3 w-3" /> Log out
              </button>
            </div>
          ) : (
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Login required to submit</div>
          )}
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-10">
        <div className="mb-7">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Submit a Listing</div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-slate-400 mt-2 max-w-2xl">{subtitle}</p>}
        </div>
        {children}
      </main>
      <footer className="max-w-3xl mx-auto px-5 pb-10 text-[11px] text-slate-600">
        Acquigine connects sellers with qualified buyers using creative acquisition structures.
        Your submission is reviewed by our team before it is visible to any acquirer, and may be declined. Contact info is only visible to admin reviewers and, upon approval, to acquirers permitted to see it.
      </footer>
    </div>
  );
}

export const inputCls =
  "w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:border-emerald-500/60 focus:outline-none transition placeholder:text-slate-600";
export const labelCls = "text-[11px] uppercase tracking-widest text-slate-500";

export function Field({ label, hint, required, children }) {
  return (
    <label className="space-y-1.5">
      <div className={labelCls}>
        {label}{required && <span className="text-emerald-400 ml-1">*</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
    </label>
  );
}

const RESEND_COOLDOWN_SECONDS = 60;

/**
 * BLOCK 170 Item C ,  the step-2 confirmation screen used by all 3 submit
 * forms. Shows the listing name + address, a link to the seller dashboard
 * (no claim token ,  the user is already an authenticated account), and an
 * honest email-send status: "sent" (provider accepted it, never claims
 * "delivered") or a retry notice with a throttled resend button when the
 * synchronous send attempt failed.
 */
export function SubmissionResult({ testid, title, address, oid, confirmationEmailSent }) {
  const [sent, setSent] = useState(!!confirmationEmailSent);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  const resend = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/listings/${oid}/resend-confirmation`);
      if (r.data?.confirmation_email_sent) {
        setSent(true);
        toast.success("Confirmation email sent.");
      } else {
        toast.message("Still could not send it. We will keep retrying automatically.");
        setCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch (err) {
      const retryAfter = err?.response?.data?.detail?.retry_after_seconds;
      if (err?.response?.status === 429 && retryAfter) {
        setCooldown(retryAfter);
        toast.error(`Please wait ${retryAfter}s before trying again.`);
      } else {
        toast.error("Could not resend right now. Please try again shortly.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ae-card p-8 text-center" data-testid={testid}>
      <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
      <h2 className="font-heading text-2xl mb-2">Submission received</h2>
      <p className="text-sm text-slate-400 max-w-md mx-auto mb-4">
        We have received your submission for <span className="text-slate-200 font-medium">{title}</span> at {address}.
        Our team reviews every submission before it becomes visible to any acquirer on the platform. Submissions may be declined.
      </p>

      {sent ? (
        <p className="text-xs text-emerald-400 mb-6" data-testid="submission-email-sent">
          A confirmation email has been sent to your account email.
        </p>
      ) : (
        <div className="p-3 rounded-sm border border-amber-500/30 bg-amber-500/5 text-xs text-amber-200 max-w-md mx-auto mb-6" data-testid="submission-email-retry-notice">
          <p>
            Your listing was received. We could not send the confirmation email yet. We will retry, and you can
            check status in your dashboard.
          </p>
          <button
            onClick={resend}
            disabled={busy || cooldown > 0}
            data-testid="submission-resend-email-btn"
            className="mt-2 inline-flex items-center gap-1.5 text-amber-300 hover:text-amber-100 disabled:opacity-40 uppercase tracking-widest text-[10px]"
          >
            <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />
            {cooldown > 0 ? `Try again in ${cooldown}s` : busy ? "Sending…" : "Try sending again"}
          </button>
        </div>
      )}

      <Link
        to="/seller/dashboard"
        data-testid="submission-dashboard-link"
        className="btn-primary inline-flex items-center justify-center text-sm"
      >
        Track this listing in your dashboard
      </Link>
    </div>
  );
}


/**
 * BLOCK 10 ,  role + attestation block used on all 3 broker/owner submit forms.
 * The pydantic validator on the backend requires the attestation matching the
 * chosen role to be true before the submission is accepted.
 */
export function RoleAttestation({ role, setRole, ownerAck, setOwnerAck, repAck, setRepAck, prefix = "sub" }) {
  return (
    <div className="pt-3 border-t border-white/8 space-y-3" data-testid={`${prefix}-role-attestation`}>
      <Field label="Who is submitting this listing?" required>
        <div className="space-y-2">
          {ROLES.map((r) => (
            <label key={r.value} className="flex items-start gap-2.5 cursor-pointer p-2 rounded-sm border border-white/8 hover:border-emerald-500/40 transition">
              <input
                type="radio"
                name={`${prefix}-role`}
                value={r.value}
                checked={role === r.value}
                onChange={() => setRole(r.value)}
                data-testid={`${prefix}-role-${r.value}`}
                className="mt-0.5"
              />
              <span className="text-sm text-slate-200">{r.label}</span>
            </label>
          ))}
        </div>
      </Field>

      {role === "owner" && (
        <label className="flex items-start gap-3 p-3 rounded-sm border border-emerald-500/30 bg-emerald-500/5 cursor-pointer" data-testid={`${prefix}-owner-attestation-wrap`}>
          <input type="checkbox" className="mt-0.5" checked={ownerAck} onChange={(e) => setOwnerAck(e.target.checked)}
                 data-testid={`${prefix}-owner-attestation`} />
          <span className="text-xs text-slate-200 leading-relaxed">
            <strong>I attest that I am the legal owner</strong> of the property or business identified above, or a person with equivalent legal authority to list it for sale.
          </span>
        </label>
      )}

      {(role === "broker" || role === "other") && (
        <label className="flex items-start gap-3 p-3 rounded-sm border border-emerald-500/30 bg-emerald-500/5 cursor-pointer" data-testid={`${prefix}-representative-attestation-wrap`}>
          <input type="checkbox" className="mt-0.5" checked={repAck} onChange={(e) => setRepAck(e.target.checked)}
                 data-testid={`${prefix}-representative-attestation`} />
          <span className="text-xs text-slate-200 leading-relaxed">
            <strong>I attest that I am an authorized representative</strong> of the owner, and that I have their written permission to submit this listing on their behalf.
          </span>
        </label>
      )}
    </div>
  );
}


/**
 * BLOCK 19 ,  Timeline dropdown. Added to every submit form after merging
 * the seller funnel into the submit pipeline.
 */
export function TimelineField({ value, onChange, prefix = "sub" }) {
  return (
    <Field label="How soon are you looking to move?">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputCls}
        data-testid={`${prefix}-timeline`}
      >
        {TIMELINES.map((t) => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </select>
    </Field>
  );
}

/**
 * BLOCK 19 ,  Reusable seller-facing review-process disclosure.
 * Replaces the old "we acquire" / "24 hour" / "direct buyer" copy across
 * all 3 submit forms so every seller sees an accurate description of what
 * happens after they submit.
 */
export function ReviewProcessNotice() {
  return (
    <div className="p-3 rounded-sm border border-amber-500/30 bg-amber-500/5 text-xs text-slate-300 leading-relaxed" data-testid="review-process-notice">
      <strong className="text-amber-300">Before you submit:</strong>
      {" "}
      Your submission will be reviewed by our team before it becomes visible to any acquirer on the platform.
      Submissions may be declined. If approved, verified acquirers will reach out directly.
      Your information is not shared publicly and is only visible to admin reviewers and, upon approval, to acquirers permitted to see it under our visibility rules.
    </div>
  );
}

/**
 * BLOCK 170 Item 1 ,  accurate, non-masking disclosure shown directly under
 * the address fields. Does not imply any masking/redaction that does not
 * exist: once approved and shared, the exact address is visible to
 * subscribers.
 */
export function AddressVisibilityNote({ testid = "address-visibility-note" }) {
  return (
    <div className="text-[11px] text-slate-500" data-testid={testid}>
      Listings are reviewed before buyers can see them. Once approved and shared, the address you enter is visible to subscribers.
    </div>
  );
}
